import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  fetchFilterSites,
  fetchFilterWells,
  fetchFilterAnalytes,
  fetchAnalyticalResults,
  fetchAnalyticalTimeseries,
  fetchGaugingResults,
  fetchGaugingTimeseries,
  fetchRiskPredictions,
  fetchPhreaticPredictions,
  fetchPhreaticWellLevels,
  fetchPhreaticGaugingTimeseries,
  fetchPolygonWellPredictions,
  fetchCostRemediation,
  fetchPlumeSpatial,
  fetchPlumeGeoJSON,
  fetchCompliance,
  fetchAnomalies,
  fetchCompare,
  fetchSavedPresets,
  createSavedPreset,
  deleteSavedPreset,
  fetchAuditLog,
  type DataExplorerFilters,
  type CompareFilters,
  type CreatePresetRequest,
} from '@/api/dataVault';

// ── Phase 1: Filters ─────────────────────────────────────────────────────

export function useFilterSites(product?: string) {
  return useQuery({
    queryKey: ['data-vault', 'filter-sites', product],
    queryFn: () => fetchFilterSites(product),
    staleTime: 60_000,
  });
}

export function useFilterWells(siteIds?: string[]) {
  return useQuery({
    queryKey: ['data-vault', 'filter-wells', siteIds],
    queryFn: () => fetchFilterWells(siteIds),
    enabled: true,
    staleTime: 30_000,
  });
}

export function useFilterAnalytes(siteIds?: string[]) {
  return useQuery({
    queryKey: ['data-vault', 'filter-analytes', siteIds],
    queryFn: () => fetchFilterAnalytes(siteIds),
    enabled: true,
    staleTime: 30_000,
  });
}

// ── Phase 1: Analytical ─────────────────────────────────────────────────

export function useAnalyticalResults(filters: DataExplorerFilters) {
  return useQuery({
    queryKey: ['data-vault', 'analytical', filters],
    queryFn: () => fetchAnalyticalResults(filters),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useAnalyticalTimeseries(filters: DataExplorerFilters) {
  return useQuery({
    queryKey: ['data-vault', 'analytical-timeseries', filters],
    queryFn: () => fetchAnalyticalTimeseries(filters),
    staleTime: 5 * 60_000,
  });
}

// ── Phase 2: Gauging ────────────────────────────────────────────────────

export function useGaugingResults(filters: DataExplorerFilters) {
  return useQuery({
    queryKey: ['data-vault', 'gauging', filters],
    queryFn: () => fetchGaugingResults(filters),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useGaugingTimeseries(filters: DataExplorerFilters) {
  return useQuery({
    queryKey: ['data-vault', 'gauging-timeseries', filters],
    queryFn: () => fetchGaugingTimeseries(filters),
    staleTime: 5 * 60_000,
  });
}

// ── Phase 3: Risk & Cost ────────────────────────────────────────────────

export function useRiskPredictions(filters: DataExplorerFilters) {
  return useQuery({
    queryKey: ['data-vault', 'risk-predictions', filters],
    queryFn: () => fetchRiskPredictions(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// Phreatic predictions run live V6 inference, so this is gated by `enabled`
// (only when the Phreatic product is active) to avoid needless ensemble runs.
export function usePhreaticPredictions(filters: DataExplorerFilters, enabled: boolean = false) {
  return useQuery({
    queryKey: ['data-vault', 'phreatic-predictions', filters],
    queryFn: () => fetchPhreaticPredictions(filters),
    staleTime: 60_000,
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Phreatic per-well water metrics for the water-level Sankey. Gated by `enabled`
// (only when Phreatic is the active product).
export function usePhreaticWellLevels(
  filters: DataExplorerFilters,
  enabled: boolean = false,
  horizonYears: number = 0,
) {
  return useQuery({
    queryKey: ['data-vault', 'phreatic-well-levels', filters, horizonYears],
    queryFn: () => fetchPhreaticWellLevels(filters, horizonYears),
    staleTime: 60_000,
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Phreatic water-level time series (observed + predicted), for the water-level
// charts when Phreatic is active. Gated by `enabled`.
export function usePhreaticGaugingTimeseries(
  filters: DataExplorerFilters,
  enabled: boolean = false,
  horizonYears: number = 0,
) {
  return useQuery({
    queryKey: ['data-vault', 'phreatic-gauging-timeseries', filters, horizonYears],
    queryFn: () => fetchPhreaticGaugingTimeseries(filters, horizonYears),
    staleTime: 60_000,
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Polygon per-well predicted concentrations for the forecast charts/Sankey.
// Gated by `enabled` (Polygon active) and only meaningful at horizon > 0.
export function usePolygonWellPredictions(
  filters: DataExplorerFilters,
  enabled: boolean = false,
  horizonYears: number = 0,
) {
  return useQuery({
    queryKey: ['data-vault', 'polygon-well-predictions', filters, horizonYears],
    queryFn: () => fetchPolygonWellPredictions(filters, horizonYears),
    staleTime: 60_000,
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useCostRemediation(filters: DataExplorerFilters) {
  return useQuery({
    queryKey: ['data-vault', 'cost-remediation', filters],
    queryFn: () => fetchCostRemediation(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ── Phase 4: Plume & Compliance ────────────────────────────────────

export function usePlumeSpatial(filters: DataExplorerFilters) {
  return useQuery({
    queryKey: ['data-vault', 'plume-spatial', filters],
    queryFn: () => fetchPlumeSpatial(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function usePlumeGeoJSON(filters: DataExplorerFilters) {
  return useQuery({
    queryKey: ['data-vault', 'plume-geojson', filters],
    queryFn: () => fetchPlumeGeoJSON(filters),
    staleTime: 60_000,
  });
}

export function useCompliance(filters: DataExplorerFilters) {
  return useQuery({
    queryKey: ['data-vault', 'compliance', filters],
    queryFn: () => fetchCompliance(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ── Phase 5: Anomalies & Compare ────────────────────────────────────

export function useAnomalies(filters: DataExplorerFilters, threshold: number = 2.0, enabled: boolean = false) {
  return useQuery({
    queryKey: ['data-vault', 'anomalies', filters, threshold],
    queryFn: () => fetchAnomalies(filters, threshold),
    enabled,
    staleTime: 60_000,
  });
}

export function useCompare(filters: CompareFilters, enabled: boolean = false) {
  return useQuery({
    queryKey: ['data-vault', 'compare', filters],
    queryFn: () => fetchCompare(filters),
    enabled,
    staleTime: 30_000,
  });
}

// ── Phase 6: Audit Log ──────────────────────────────────────────────────

export function useAuditLog(page: number = 1, pageSize: number = 25, enabled: boolean = false) {
  return useQuery({
    queryKey: ['data-vault', 'audit-log', page, pageSize],
    queryFn: () => fetchAuditLog(page, pageSize),
    enabled,
    staleTime: 15_000,
  });
}

// ── Phase 3: Saved Presets ──────────────────────────────────────────────

export function useSavedPresets() {
  return useQuery({
    queryKey: ['data-vault', 'presets'],
    queryFn: fetchSavedPresets,
    staleTime: 60_000,
  });
}

export function useCreatePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePresetRequest) => createSavedPreset(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['data-vault', 'presets'] });
    },
  });
}

export function useDeletePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSavedPreset(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['data-vault', 'presets'] });
    },
  });
}
